import React, { useEffect, useState } from "react";
import { Building2, Users, Home, Briefcase, ClipboardList, ShieldCheck, Wrench, LineChart, Loader2, LogIn } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const ICONS = {
  admin: LineChart, bailleur: Building2, locataire: Home, agent: Briefcase,
  gestionnaire: ClipboardList, assureur: ShieldCheck, artisan: Wrench, courtier: Users,
};
const DESC = {
  admin: "Administration : profils, accès, menus et offres.",
  bailleur: "Gestion de biens, locataires, quittances et finances.",
  locataire: "Espace locataire : quittances, assurances, messagerie.",
  agent: "Agent immobilier : accès complet à la gestion locative.",
  gestionnaire: "Gestionnaire : suivi des biens et des locataires.",
  assureur: "Assureur : produits d'assurance et messagerie.",
  artisan: "Artisan : activité, interventions et devis.",
  courtier: "Courtier : accompagnement financement et biens.",
};

export default function Demo() {
  const { setUser } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [busy, setBusy] = useState("");
  const [available, setAvailable] = useState(true);

  useEffect(() => {
    api.get("/auth/demo-accounts")
      .then((r) => setAccounts(r.data || []))
      .catch((e) => { if (e?.response?.status === 404) setAvailable(false); else toast.error("Chargement impossible"); });
  }, []);

  const login = async (role) => {
    setBusy(role);
    try {
      const res = await api.post("/auth/demo-login", { role });
      setUser(res.data);
      toast.success(`Connecté en tant que ${res.data.name}`);
      window.location.href = "/app";
    } catch (e) { if (e?.response?.status === 404) setAvailable(false); toast.error(e?.response?.data?.detail || "Connexion impossible"); }
    finally { setBusy(""); }
  };

  if (!available) {
    return (
      <div className="min-h-screen bg-brand-softer flex items-center justify-center px-5" data-testid="demo-unavailable">
        <div className="bg-white rounded-2xl border border-brand-soft p-10 text-center max-w-md">
          <div className="w-14 h-14 rounded-2xl bg-brand-lime/30 text-brand-greenDeep flex items-center justify-center mx-auto mb-5"><ShieldCheck className="w-7 h-7" /></div>
          <h1 className="font-display text-2xl font-semibold text-brand-greenDeep">Démonstration désactivée</h1>
          <p className="mt-3 text-brand-green/60">L'espace de démonstration n'est pas accessible en production. Merci de vous connecter avec votre compte.</p>
          <a href="/login" className="mt-6 inline-flex items-center justify-center gap-2 py-3 px-6 rounded-full bg-brand-greenDeep hover:bg-brand-emerald text-white font-semibold text-sm transition-colors" data-testid="demo-goto-login"><LogIn className="w-4 h-4" /> Se connecter</a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-softer py-16 px-5" data-testid="demo-page">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-10">
          <p className="text-brand-limeDark font-semibold mb-2">Espace de démonstration</p>
          <h1 className="font-display text-4xl font-semibold text-brand-greenDeep">Testez chaque profil</h1>
          <p className="mt-3 text-brand-green/60 max-w-xl mx-auto">Choisissez un profil pour vous connecter instantanément et explorer l'application avec ses menus et droits dédiés.</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {accounts.map((a) => {
            const Icon = ICONS[a.role] || Users;
            return (
              <div key={a.role} className="bg-white rounded-2xl border border-brand-soft p-6 flex flex-col" data-testid={`demo-card-${a.role}`}>
                <div className="w-12 h-12 rounded-2xl bg-brand-lime/30 text-brand-greenDeep flex items-center justify-center mb-4"><Icon className="w-6 h-6" /></div>
                <h3 className="font-display text-lg font-semibold text-brand-greenDeep">{a.label}</h3>
                <p className="text-sm text-brand-green/60 mt-1 flex-1">{DESC[a.role] || ""}</p>
                <p className="text-xs text-brand-green/40 mt-2">{a.email}</p>
                <button onClick={() => login(a.role)} disabled={busy === a.role} className="mt-4 inline-flex items-center justify-center gap-2 w-full py-3 rounded-full bg-brand-greenDeep hover:bg-brand-emerald text-white font-semibold text-sm disabled:opacity-60 transition-colors" data-testid={`demo-login-${a.role}`}>
                  {busy === a.role ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogIn className="w-4 h-4" />} Se connecter
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
